import { RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import { RANGES } from "@/pages/admin/data";
import { BUSINESS_TYPE_OPTIONS } from "../data";

export interface FilterChip {
  label: string;
  onRemove: () => void;
}

interface FilterBarProps {
  search: string;
  onSearch: (v: string) => void;
  type: string;
  onType: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  rangeOpen?: boolean;
  onRangeOpen?: (v: boolean) => void;
  sort: string;
  onSort: (v: string) => void;
  onReset: () => void;
  chips: FilterChip[];
}

export default function FilterBar({
  search,
  onSearch,
  type,
  onType,
  range,
  onRange,
  sort,
  onSort,
  onReset,
  chips,
}: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeCount =
    (type ? 1 : 0) + (range !== "Last 30 Days" && range !== "All Time" ? 1 : 0) + (sort !== "Newest First" ? 1 : 0);

  const typeOptions = [
    { value: "", label: "All Business Types" },
    ...BUSINESS_TYPE_OPTIONS,
  ];

  const rangeOptions = RANGES.map((r) => ({ value: r, label: r }));

  const sortOptions = [
    { value: "Newest First", label: "Newest First" },
    { value: "Oldest First", label: "Oldest First" },
  ];

  const mobileFilters = (
    <div className="flex flex-col gap-4">
      <div>
        <span className="mb-1.5 block text-[13px] font-medium text-ink">Business Type</span>
        <Select
          value={type}
          onChange={(e) => onType(e.target.value)}
          options={typeOptions}
        />
      </div>
      <div>
        <span className="mb-1.5 block text-[13px] font-medium text-ink">Submission Date</span>
        <Select
          value={range}
          onChange={(e) => onRange(e.target.value)}
          options={rangeOptions}
        />
      </div>
      <div>
        <span className="mb-1.5 block text-[13px] font-medium text-ink">Sort</span>
        <Select
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          options={sortOptions}
        />
      </div>
      <div className="flex justify-end gap-2.5 pt-2 border-t border-line">
        <Button variant="secondary" onClick={onReset}>
          Reset
        </Button>
        <Button onClick={() => setMobileOpen(false)}>Apply Filters</Button>
      </div>
    </div>
  );

  return (
    <div className="rounded-[10px] border border-line bg-card p-4 shadow-card">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-1 items-center gap-3">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            prefixIcon={<Search className="h-4 w-4" />}
            placeholder="Search by shop name, owner, business name or PAN"
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => setMobileOpen(true)}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
            className="lg:hidden shrink-0"
          >
            Filters
            {activeCount > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-[11px] font-semibold text-white">
                {activeCount}
              </span>
            )}
          </Button>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="w-48">
            <Select
              value={type}
              onChange={(e) => onType(e.target.value)}
              options={typeOptions}
            />
          </div>

          <div className="w-40">
            <Select
              value={range}
              onChange={(e) => onRange(e.target.value)}
              options={rangeOptions}
            />
          </div>

          <div className="w-36">
            <Select
              value={sort}
              onChange={(e) => onSort(e.target.value)}
              options={sortOptions}
            />
          </div>

          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {chips.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-600"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                className="flex h-4 w-4 items-center justify-center rounded-full text-primary-600/70 hover:bg-primary-600/10 hover:text-primary-600 cursor-pointer"
                aria-label={`Remove ${c.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Modal open={mobileOpen} onClose={() => setMobileOpen(false)} title="Filters" size="sm">
        {mobileFilters}
      </Modal>
    </div>
  );
}